import {
  AnalysisResult,
  AnalysisResultCriteriaResultEnum,
  CriteriaQualifier,
  ScreenerAnalysisOverride,
  VisualReference
} from '@/types/crm-screener';
import { Criteria, CriteriaCategory } from '@/types/crm-screener-api';

export enum AIAnalysisCustomStatus {
  Pass = 'Pass',
  Fail = 'Fail',
  Unknown = 'Human Review Recommended',
  FollowUp = 'Follow-Up',
  Informational = 'Informational'
}

export class AnalysisResultModel implements AnalysisResult {
  id: string;
  lead_id: string;
  query: string;
  criteria_id?: string | undefined;
  criteria_result: AnalysisResultCriteriaResultEnum;
  reasoning: string;
  date: string;
  qualifier: CriteriaQualifier;
  aiAnalysisCustomStatus: AIAnalysisCustomStatus;
  deleted_at?: string | undefined;
  visual_references?: VisualReference[];
  is_medication_rule: boolean;
  latest_override?: ScreenerAnalysisOverride;
  has_override_history?: boolean;

  criteria?: Criteria;
  category?: CriteriaCategory;

  public readonly searchText: string;

  constructor(payload: AnalysisResult) {
    Object.assign(this, payload);

    this.aiAnalysisCustomStatus = this.mapCriteriaResultToCustomStatus(
      this.criteria_result,
      this.qualifier
    );
    this.searchText = [this.query, this.aiAnalysisCustomStatus, this.reasoning]
      .join(' ')
      .toLowerCase();
  }

  public getRuleChipLabel(): string {
    return this.qualifier === CriteriaQualifier.DealBreaker
      ? 'Dealbreaker'
      : 'Maybe, But Follow-Up Needed';
  }

  public isNotInformational(): boolean {
    return this.qualifier !== 'Informational';
  }

  public isInformational(): boolean {
    return this.qualifier === 'Informational';
  }

  public hasOverride(): boolean {
    return this.latest_override?.is_revert === false;
  }

  public hasOverrideHistory(): boolean {
    return this.has_override_history ?? false;
  }

  public getOriginalStatus(): AIAnalysisCustomStatus | null {
    if (!this.hasOverride() || !this.criteria_result) {
      return null;
    }
    return this.mapCriteriaResultToCustomStatus(
      this.criteria_result,
      this.qualifier
    );
  }

  public getOverriddenByUserName(): string | null {
    const user = this.latest_override?.created_by_user;
    if (!user) {
      return null;
    }
    return (
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || null
    );
  }

  public getDisplayStatus(): AIAnalysisCustomStatus {
    if (this.hasOverride() && this.latest_override?.override_criteria_result) {
      return this.mapOverrideCriteriaResultToCustomStatus(
        this.latest_override.override_criteria_result
      );
    }
    return this.aiAnalysisCustomStatus;
  }

  public getDisplayText(): string {
    if (this.hasOverride() && this.latest_override?.override_comment) {
      return 'Override Comment: ' + this.latest_override.override_comment;
    }
    return this.reasoning;
  }

  public getOverrideReasonText(): string {
    if (this.hasOverride() && this.latest_override?.override_reason) {
      return (
        'Override Reason: ' + this.latest_override.override_reason.toString()
      );
    }
    return '';
  }

  public getOriginalDisplayStatus(): AIAnalysisCustomStatus {
    return this.aiAnalysisCustomStatus;
  }

  public getOriginalDisplayText(): string {
    return this.reasoning;
  }

  public shouldShowOverrideIndicator(): boolean {
    return this.hasOverride();
  }

  public mapOverrideCriteriaResultToCustomStatus(
    criteriaResult: AnalysisResultCriteriaResultEnum
  ): AIAnalysisCustomStatus {
    let customAIAnalysisStatus;

    switch (true) {
      case criteriaResult === AnalysisResultCriteriaResultEnum.Pass:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Pass;
        break;
      case criteriaResult === AnalysisResultCriteriaResultEnum.Fail:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Fail;
        break;
      case criteriaResult === AnalysisResultCriteriaResultEnum.Unknown:
        customAIAnalysisStatus = AIAnalysisCustomStatus.FollowUp;
        break;
      default:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Unknown;
        break;
    }

    return customAIAnalysisStatus;
  }

  public mapCriteriaResultToCustomStatus(
    criteriaResult: AnalysisResultCriteriaResultEnum,
    qualifier: CriteriaQualifier
  ): AIAnalysisCustomStatus {
    let customAIAnalysisStatus;

    switch (true) {
      case qualifier === CriteriaQualifier.Informational:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Informational;
        break;
      case qualifier === CriteriaQualifier.DealBreaker &&
        criteriaResult === AnalysisResultCriteriaResultEnum.Pass:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Pass;
        break;
      case qualifier === CriteriaQualifier.DealBreaker &&
        criteriaResult === AnalysisResultCriteriaResultEnum.Fail:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Fail;
        break;
      case qualifier === CriteriaQualifier.DealBreaker &&
        criteriaResult === AnalysisResultCriteriaResultEnum.Unknown:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Unknown;
        break;
      case qualifier === CriteriaQualifier.Warning &&
        criteriaResult === AnalysisResultCriteriaResultEnum.Pass:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Pass;
        break;
      case qualifier === CriteriaQualifier.Warning &&
        criteriaResult === AnalysisResultCriteriaResultEnum.Fail:
        customAIAnalysisStatus = AIAnalysisCustomStatus.FollowUp;
        break;
      case qualifier === CriteriaQualifier.Warning &&
        criteriaResult === AnalysisResultCriteriaResultEnum.Unknown:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Unknown;
        break;
      default:
        customAIAnalysisStatus = AIAnalysisCustomStatus.Unknown;
        break;
    }

    return customAIAnalysisStatus;
  }

  public static fromOverrideData(
    originalResult: AnalysisResultModel,
    overrideData: any
  ): AnalysisResultModel {
    const overrideResult = new AnalysisResultModel({
      ...originalResult,
      criteria_result: overrideData.override_criteria_result,
      reasoning: originalResult.reasoning,
      date: overrideData.createdAt || originalResult.date,
      latest_override: overrideData
    });

    return overrideResult;
  }
}
